import { FormEvent, Fragment, useCallback, useEffect, useState } from "react";
import CurrencyInput from "react-currency-input-field";
import {
  ApiError,
  LocationsService,
  OrganizationMeetingLocationType,
  USState,
} from "client/openapi";
import type { OrganizationMeetingLocationAdminResponse } from "client/openapi/models/OrganizationMeetingLocationAdminResponse";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { COMPACT_INPUT, CompactRadio } from "components/Inputs/compact";
import Notifications from "util/notifications";
import { LABEL, SECTION, SECTION_TEXT, SECTION_TITLE } from "./settingsStyles";

const { IN_PERSON } = OrganizationMeetingLocationType;

const TYPE_LABELS: Record<OrganizationMeetingLocationType, string> = {
  [OrganizationMeetingLocationType.IN_PERSON]: "In person",
  [OrganizationMeetingLocationType.ONLINE]: "Online",
};

const dollars = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const errorText = (e: ApiError): string =>
  e.body?.detail ?? "Something went wrong. Please try again.";

type Draft = {
  name: string;
  location_type: OrganizationMeetingLocationType;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: USState | null;
  zip: string;
  link: string;
  // Dollars as typed; the server stores cents.
  bonus: string;
};

type TextKey =
  | "name"
  | "address_line_1"
  | "address_line_2"
  | "city"
  | "zip"
  | "link";

function toDraft(location?: OrganizationMeetingLocationAdminResponse): Draft {
  const bonus = location?.default_travel_bonus_amount;
  return {
    name: location?.name ?? "",
    location_type: location?.location_type ?? IN_PERSON,
    address_line_1: location?.address_line_1 ?? "",
    address_line_2: location?.address_line_2 ?? "",
    city: location?.city ?? "",
    state: location?.state ?? null,
    zip: location?.zip ?? "",
    link: location?.link ?? "",
    bonus: bonus == null ? "" : (bonus / 100).toFixed(2),
  };
}

// The server wants an address and no link for in-person places, and a link
// and no address for online ones, so the other kind's fields go as null.
function toBody(draft: Draft) {
  const inPerson = draft.location_type === IN_PERSON;
  const address = (text: string) => (inPerson && text.trim()) || null;
  return {
    name: draft.name.trim(),
    location_type: draft.location_type,
    address_line_1: address(draft.address_line_1),
    address_line_2: address(draft.address_line_2),
    city: address(draft.city),
    state: inPerson ? draft.state : null,
    zip: address(draft.zip),
    link: inPerson ? null : draft.link.trim(),
    default_travel_bonus_amount:
      draft.bonus === "" ? null : Math.round(Number(draft.bonus) * 100),
  };
}

/** An in-person place's address, or an online place's link. */
function whereOf(location: OrganizationMeetingLocationAdminResponse): string {
  return (
    (location.location_type === IN_PERSON
      ? [location.address_line_1, location.city].filter(Boolean).join(", ")
      : location.link) ?? ""
  );
}

const CELL = "py-2.5 pr-3 align-middle";

/**
 * Organization Settings' Locations tab: the org's meeting places in a table
 * (name, type, address or link, travel bonus), with edit and add in a
 * compact form in the table, and delete. It loads and saves on its own.
 */
export default function LocationSettings({ orgId }: { orgId: number }) {
  const [locations, setLocations] =
    useState<OrganizationMeetingLocationAdminResponse[]>();
  // The id of the location being edited, or "new" while one is being added.
  const [editing, setEditing] = useState<number | "new">();
  const [deleteError, setDeleteError] = useState<{
    id: number;
    text: string;
  }>();

  const load = useCallback(
    () =>
      LocationsService.getLocationsByOrg({ orgId })
        .then((list) =>
          setLocations(list.sort((a, b) => a.name.localeCompare(b.name)))
        )
        .catch((e) => Notifications.error(errorText(e))),
    [orgId]
  );

  useEffect(() => {
    load();
  }, [load]);

  const close = () => setEditing(undefined);

  const onSaved = () => {
    close();
    load();
  };

  const remove = (id: number) => {
    setDeleteError(undefined);
    LocationsService.deleteLocation({ locationId: id })
      .then(load)
      .catch((e) => setDeleteError({ id, text: errorText(e) }));
  };

  const formRow = (location?: OrganizationMeetingLocationAdminResponse) => (
    <tr>
      <td colSpan={5} className="py-2">
        <LocationForm
          orgId={orgId}
          location={location}
          onSaved={onSaved}
          onCancel={close}
        />
      </td>
    </tr>
  );

  return (
    <section className={`${SECTION} max-w-3xl`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className={SECTION_TITLE}>Locations</h3>
          <p className={SECTION_TEXT}>
            Places meetings can be booked. In-person places show in See
            available rooms.
          </p>
        </div>
        {editing !== "new" && (
          <Button
            size={ButtonSize.SMALL}
            color={ButtonColor.GREEN}
            fill={ButtonFill.HOLLOW}
            extraClasses="shrink-0"
            onClick={() => setEditing("new")}
          >
            Add location
          </Button>
        )}
      </div>

      {!locations ? (
        <p className="mt-4 text-sm text-gray-500">Loading…</p>
      ) : (
        <>
          <table className="mt-4 w-full table-fixed text-sm">
            <thead>
              <tr className={`border-b border-gray-200 text-left ${LABEL}`}>
                <th className="w-[24%] pb-2 pr-3">Name</th>
                <th className="w-[12%] pb-2 pr-3">Type</th>
                <th className="w-[30%] pb-2 pr-3">Address or link</th>
                <th className="w-[16%] whitespace-nowrap pb-2 pr-3">
                  Travel bonus
                </th>
                <th className="pb-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {locations.map((location) =>
                editing === location.id ? (
                  <Fragment key={location.id}>{formRow(location)}</Fragment>
                ) : (
                  <Fragment key={location.id}>
                    <tr>
                      <td
                        className={`${CELL} truncate font-medium text-neutral-900`}
                        title={location.name}
                      >
                        {location.name}
                      </td>
                      <td className={CELL}>
                        {TYPE_LABELS[location.location_type]}
                      </td>
                      <td
                        className={`${CELL} truncate text-gray-600`}
                        title={whereOf(location)}
                      >
                        {whereOf(location)}
                      </td>
                      <td className={CELL}>
                        {location.default_travel_bonus_amount == null
                          ? "—"
                          : dollars.format(
                              location.default_travel_bonus_amount / 100
                            )}
                      </td>
                      <td className={`${CELL} whitespace-nowrap text-right`}>
                        <div className="inline-flex gap-2">
                          <Button
                            size={ButtonSize.EXTRA_SMALL}
                            color={ButtonColor.BLACK}
                            fill={ButtonFill.HOLLOW}
                            aria-label={`Edit ${location.name}`}
                            onClick={() => setEditing(location.id)}
                          >
                            Edit
                          </Button>
                          <Button
                            size={ButtonSize.EXTRA_SMALL}
                            color={ButtonColor.RED}
                            fill={ButtonFill.HOLLOW}
                            aria-label={`Delete ${location.name}`}
                            onClick={() => remove(location.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {deleteError?.id === location.id && (
                      <tr>
                        <td colSpan={5} className="pb-2 text-xs text-red-600">
                          {deleteError.text}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              )}
              {editing === "new" && formRow()}
            </tbody>
          </table>
          {locations.length === 0 && editing !== "new" && (
            <p className="py-4 text-sm text-gray-500">No locations yet.</p>
          )}
        </>
      )}
    </section>
  );
}

/** The add and edit form. It saves on its own, then calls onSaved. */
function LocationForm({
  orgId,
  location,
  onSaved,
  onCancel,
}: {
  orgId: number;
  location?: OrganizationMeetingLocationAdminResponse;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(location));
  const [saving, setSaving] = useState<boolean>(false);

  const inPerson = draft.location_type === IN_PERSON;
  const canSave =
    !saving &&
    draft.name.trim() !== "" &&
    (inPerson ? draft.address_line_1 : draft.link).trim() !== "";

  const save = (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const body = toBody(draft);
    (location
      ? LocationsService.updateLocation({
          locationId: location.id,
          requestBody: body,
        })
      : LocationsService.createLocation({
          requestBody: { org_id: orgId, ...body },
        })
    )
      .then(onSaved)
      .catch((err) => {
        Notifications.error(errorText(err));
        setSaving(false);
      });
  };

  const textField = (key: TextKey, label: string, span: string) => (
    <label className={`flex flex-col gap-1 ${span}`}>
      <span className={LABEL}>{label}</span>
      <input
        type="text"
        className={`${COMPACT_INPUT} w-full`}
        value={draft[key]}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
      />
    </label>
  );

  return (
    <form
      onSubmit={save}
      className="grid grid-cols-12 items-end gap-x-3 gap-y-2.5 rounded-lg border border-gray-200 bg-gray-50 p-3"
    >
      {textField("name", "Name", "col-span-7")}
      <div className="col-span-5 flex h-8 items-center gap-4">
        {Object.values(OrganizationMeetingLocationType).map((type) => (
          <CompactRadio
            key={type}
            id={`location-type-${type}`}
            name="location-type"
            checked={draft.location_type === type}
            onChange={() => setDraft({ ...draft, location_type: type })}
          >
            {TYPE_LABELS[type]}
          </CompactRadio>
        ))}
      </div>
      {inPerson ? (
        <>
          {textField("address_line_1", "Address line 1", "col-span-6")}
          {textField("address_line_2", "Address line 2", "col-span-6")}
          {textField("city", "City", "col-span-5")}
          <label className="col-span-3 flex flex-col gap-1">
            <span className={LABEL}>State</span>
            <select
              className={`${COMPACT_INPUT} w-full pr-7`}
              value={draft.state ?? ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  state: (e.target.value || null) as USState | null,
                })
              }
            >
              <option value="" />
              {Object.values(USState).map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </label>
          {textField("zip", "ZIP", "col-span-4")}
        </>
      ) : (
        textField("link", "Link", "col-span-12")
      )}
      <label className="col-span-4 flex flex-col gap-1">
        <span className={LABEL}>Default travel bonus</span>
        <CurrencyInput
          className={`${COMPACT_INPUT} w-28`}
          prefix="$"
          placeholder="None"
          decimalsLimit={2}
          allowNegativeValue={false}
          value={draft.bonus}
          onValueChange={(value) => setDraft({ ...draft, bonus: value ?? "" })}
        />
      </label>
      <div className="col-span-8 flex justify-end gap-2">
        <Button
          type="submit"
          size={ButtonSize.EXTRA_SMALL}
          color={canSave ? ButtonColor.GREEN : ButtonColor.GRAY}
          disabled={!canSave}
        >
          Save
        </Button>
        <Button
          type="button"
          size={ButtonSize.EXTRA_SMALL}
          color={ButtonColor.GRAY}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
